import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import TeacherIcon from 'assets/images/icons/avatar-teacher.webp';
import imgExample from 'assets/images/icons/brain.webp';
import imgExercise from 'assets/images/icons/contract.webp';
import imgVideo from 'assets/images/icons/movies.webp';
import IndicatorsOccurrencesTable from 'components/IndicatorsOccurrencesTable';
import RootModal from 'components/RootModal';
import {fromJS} from 'immutable';
import {CircularProgressbar, ProgressBar} from 'letrus-ui';
import {DevolutiveCard, NewButton, Modal, TitleText} from 'components/ui';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {
  getCompositionIsFundII,
  getIletrusScore,
} from 'store/reducers/compositions';
import {getLearningPath} from 'store/reducers/learningPath';
import {ApplicationState} from 'store/rootReducer';
import convertILetrusToLevel from 'utils/functions/convertILetrusToLevel';
import {getShownStatScore} from 'utils/functions/showStatScore';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {EventData} from 'utils/hooks/useTrackEvent/types';
import {getScoreColor} from 'utils/libs/get-human-score';
import {CompositionDetailExtended} from 'utils/types/compositions';
import {HighlightCategory} from 'utils/types/highlight';
import {LearningPathTemp} from 'utils/types/learningPath';
import './styles.scss';

export interface CompositionProps extends CompositionDetailExtended {
  isLoading: boolean;
}

const statGoals = {
  conectivos: {
    goal: 15,
    max: 15,
  },
  agentes_sociais: {
    goal: 6,
    max: 6,
  },
  propositivos: {
    goal: 4,
    max: 4,
  },
  paragrafos: {
    goal: 4,
    max: 5,
  },
  sentencas: {
    goal: 18,
    max: 18,
  },
  virgulas: {
    goal: 30,
    max: 30,
  },
  pontos: {
    goal: 18,
    max: 18,
  },
  erros_ortograficos: {
    goal: 0,
    max: 0,
  },
  oralidades: {
    goal: 0,
    max: 0,
  },
  palavras: {
    goal: 340,
    max: 340,
  },
};

interface Modal {
  open: boolean;
  content?: any;
  className: string;
  isPedagogicalContent?: boolean;
}

export const emptyHighlight: HighlightCategory = {
  highlight: fromJS([]),
  highlightName: '',
};

interface OwnProps {
  wordsNumber?: number;
  jsonStatsAsync: ImmutableMap<any>;
  composition: ImmutableMap<CompositionProps>;
  hideCircularProgressBar?: boolean;
  setHighlight?: (highlightCategory: HighlightCategory) => void;
  showIndicatorsButtonModal?: boolean;
  isHidingTitle?: boolean;
  isHidingPrincipalDeviations?: boolean;
  onUnload?(): void;
}

interface StateProps {
  iLetrusScore: number;
  learningPath?: LearningPathTemp;
  isFundII: boolean;
}

type StatisticsSideBarProps = OwnProps & StateProps;

function StatisticsSidebar({
  jsonStatsAsync,
  wordsNumber,
  composition,
  iLetrusScore = 0,
  hideCircularProgressBar = false,
  setHighlight,
  isHidingTitle,
  isHidingPrincipalDeviations,
  isFundII,
  showIndicatorsButtonModal,
  learningPath,
  onUnload,
}: StatisticsSideBarProps): JSX.Element {
  const [modal, setModal] = useState<Modal>({
    open: false,
    content: '',
    className: '',
    isPedagogicalContent: false,
  });
  const [activeProgressBar, setActiveProgressBar] = useState('');

  const {trackEvent} = useTrackEvent();

  useEffect(() => {
    return () => {
      setHighlight?.(emptyHighlight);
      onUnload?.();
    };
  }, []);

  const eventData: EventData = {
    composition: {
      composition_id: Number(composition?.get('id')) ?? 0,
    },
    test: {
      test_id: Number(composition?.getIn(['test', 'id'])) ?? 0,
    },
    ...(learningPath
      ? {
          learningPathUserInstance: {
            learning_path_user_instance_id: learningPath.id,
            step_id:
              learningPath.steps?.find(
                (step) => step.order === learningPath.step_number,
              )?.id || 0,
          },
        }
      : {}),
    review: {
      review_id: composition.get('reviews')?.first() ?? 0,
    },
  };

  const setPedagogicalModal = (pedagogicalContent: any) => {
    setModal({
      open: true,
      content: <iframe title="Conteúdo pedagógico" src={pedagogicalContent} />,
      className: 'iframe',
      isPedagogicalContent: true,
    });
  };

  const genre = composition.get('genre');

  const isEnem = genre && genre.get('tag') === 'ENEM';
  const level = convertILetrusToLevel(iLetrusScore);
  const levelColor = getScoreColor(iLetrusScore || 0);

  const oralidades =
    jsonStatsAsync.getIn(['jsonMetadata', 'oralidades']) || fromJS({});

  const erros_ortograficos =
    jsonStatsAsync.getIn(['jsonMetadata', 'erros_ortograficos']) || fromJS({});

  const feedbacks = jsonStatsAsync.get('feedbacks') || fromJS({});
  const comments = jsonStatsAsync.get('comments') || fromJS({});
  const shownStats = genre?.get('shown_stats');

  const oralityDeviations = oralidades.size
    ? oralidades.map((oralidade: any) => oralidade.get('text'))
    : fromJS({});

  const orthographicDeviations = erros_ortograficos
    ? erros_ortograficos.map((erro_ortografico: any) =>
        erro_ortografico.get('text'),
      )
    : fromJS({});

  const hasDeviations =
    oralityDeviations.size > 0 || orthographicDeviations.size > 0;

  const resultIndicators = shownStats
    ?.sort(
      (currentStat: any, nextStat: any) =>
        currentStat.sort_order - nextStat.sort_order,
    )
    ?.map((shownStat: any) => {
      const label = shownStat.getIn(['marker', 'label']);
      const description = shownStat.get('description');

      const automaticMarkerComment =
        (comments &&
          comments.size > 0 &&
          comments.toJS()[shownStat.getIn(['marker', 'varname'])]) ||
        {};

      const name = shownStat.getIn(['marker', 'varname']);
      const goal =
        shownStat.get('goal') != null
          ? {
              goal: shownStat.get('goal'),
              max: shownStat.get('ideal_range_max'),
              min: shownStat.get('ideal_range_min'),
            }
          : (statGoals as any)[name];

      const value =
        shownStat.getIn(['marker', 'varname']) === 'palavras' && wordsNumber
          ? wordsNumber
          : getShownStatScore(jsonStatsAsync.toJS(), shownStat.toJS());

      const statLevelHasLength =
        shownStat.stat_level && shownStat.stat_level.length > 0;

      const new_order = statLevelHasLength
        ? shownStat.stat_level.map((level: any) => {
            if (
              level.min_range <= shownStat.score.value &&
              shownStat.score.value <= level.max_range
            ) {
              return level.level;
            }

            return 10;
          })
        : null;

      return {
        label,
        title: shownStat.get('public_name'),
        goal,
        varname: shownStat.getIn(['marker', 'varname']),
        automaticMarkerComment,
        description,
        value,
        stat_level: shownStat.get('stat_level'),
        new_order,
      };
    });

  const onClickScore = (name: string) => {
    if (!setHighlight) {
      return;
    }

    if (activeProgressBar === name) {
      setActiveProgressBar('');
      setHighlight(emptyHighlight);
      return;
    }

    trackEvent('has_opened_structure_report_indicator', eventData);

    const markersHighlight = jsonStatsAsync.get('markersHighlight');

    const highlight =
      markersHighlight && markersHighlight.get(name)
        ? {
            highlight:
              jsonStatsAsync && jsonStatsAsync.getIn(['jsonMetadata', name]),
            highlightName: name,
          }
        : emptyHighlight;
    setHighlight(highlight);
    setActiveProgressBar(name);
  };

  const orderedIndicators = resultIndicators?.sort(
    (currentIndicator: any, nextIndicator: any) =>
      currentIndicator.new_order - nextIndicator.new_order,
  );

  const iLetrus = Number(
    (jsonStatsAsync.get('numeric_metadata') &&
    jsonStatsAsync.getIn(['numeric_metadata', 'letrus_score'])
      ? jsonStatsAsync.getIn(['numeric_metadata', 'letrus_score']) * 100
      : 0
    ).toFixed(1),
  );

  const renderProgress = () => {
    return orderedIndicators?.map((indicator: any) => {
      const {
        varname,
        value,
        goal: {goal, max, min},
        label,
        title,
        description,
        automaticMarkerComment: {activity_url, example_url, video_url, comment},
      } = indicator;

      const hasHelpContent = activity_url || example_url || video_url;

      return (
        <div
          className="StatisticsSidebar__Body__ProgressBar__Item"
          key={varname}
        >
          <ProgressBar
            goal={goal}
            label={title}
            max={max}
            min={min}
            value={value}
            onClick={() => onClickScore(varname)}
            isOpen={varname === activeProgressBar}
          >
            <div
              className={`StatisticsSidebar__Body__ProgressBar__Item__content ${
                comment && isFundII ? 'show' : ''
              }`}
            >
              <DevolutiveCard showShadows>
                <p
                  dangerouslySetInnerHTML={{
                    __html: comment,
                  }}
                />
              </DevolutiveCard>
            </div>
            <div
              className={`StatisticsSidebar__Body__ProgressBar__Item__content ${
                description ? 'show' : ''
              }`}
            >
              <DevolutiveCard showShadows>
                <h3>O que são {label}?</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              </DevolutiveCard>
            </div>
            <div
              className={`StatisticsSidebar__Body__ProgressBar__Item__content ${
                hasHelpContent ? 'show' : ''
              }`}
            >
              <DevolutiveCard showShadows>
                <p>
                  Separamos esses materiais para você se aprofundar nos estudos:
                </p>
                <div className="HelpContentContainer">
                  <div
                    className={`HelpContentContainer__item ${
                      example_url ? 'show' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setPedagogicalModal(example_url)}
                    >
                      <img src={imgExample} alt="Exemplo" />
                    </button>
                    <div>Exemplos</div>
                  </div>

                  <div
                    className={`HelpContentContainer__item ${
                      activity_url ? 'show' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setPedagogicalModal(activity_url)}
                    >
                      <img src={imgExercise} alt="Exercício" />
                    </button>
                    <div>Exercícios</div>
                  </div>

                  <div
                    className={`HelpContentContainer__item ${
                      video_url ? 'show' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setPedagogicalModal(video_url)}
                    >
                      <img src={imgVideo} alt="Vídeo" />
                    </button>
                    <div>Vídeos</div>
                  </div>
                </div>
              </DevolutiveCard>
            </div>
          </ProgressBar>
          {showIndicatorsButtonModal &&
            (varname === 'erros_ortograficos' ||
              varname === 'erros_gramaticais') && (
              <RootModal
                modalTitle={title}
                modalContent={
                  <IndicatorsOccurrencesTable
                    list={jsonStatsAsync?.getIn([
                      'markers_occurrences',
                      varname,
                    ])}
                  />
                }
              >
                <NewButton>Ver Desvios</NewButton>
              </RootModal>
            )}
        </div>
      );
    });
  };

  if (isFundII) {
    return (
      <div className="StatisticsSidebar">
        <div className="StatisticsSidebar__Header__Card">
          <img
            alt="Ícone de professor"
            className="StatisticsSidebar__Header__Card__Image"
            src={TeacherIcon}
          />
          <p className="StatisticsSidebar__Header__Card__Info">
            Nossos professores estão corrigindo a sua atividade e você poderá
            ver os resultados em até três dias! Fique de olho!
          </p>
          <p className="StatisticsSidebar__Header__Card__InfoDescription">
            Na correção, você poderá: <br />- Ver anotações sobre pontos
            específicos do seu texto; <br />- Acessar recomendações sobre como
            melhorar sua escrita.
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="StatisticsSidebar">
      <div className="StatisticsSidebar__Header">
        {composition.size &&
        composition.getIn(['test', 'test_type']) !== 'Especial' ? (
          <>
            {!isHidingTitle && (
              <TitleText tag="h1" title="Estrutura do seu texto" />
            )}
            {iLetrusScore ? (
              <div className="StatisticsSidebar__Header__RightSide">
                <div
                  style={{backgroundColor: `#${levelColor}`}}
                  className="StatisticsSidebar__Header__RightSide__Level"
                >
                  Nível {level}
                </div>
                <button
                  type="button"
                  className="StatisticsSidebar__Header__RightSide__Icon"
                  onClick={() =>
                    setModal({
                      open: true,
                      className: 'Level',
                      content: (
                        <div className="StatisticsSidebar__Info__Level">
                          <div
                            style={{backgroundColor: `#${levelColor}`}}
                            className="StatisticsSidebar__Info__Level__middle"
                          >
                            Níveis
                          </div>
                          <div className="StatisticsSidebar__Info__Level__content">
                            Escala de avaliação de um texto elaborada a partir
                            da estrutura do gênero trabalhado. Ela varia de 1 a
                            5, sendo que o nível 1 representa os textos que mais
                            se distanciam da estrutura esperada e o 5
                            respresenta os textos que mais se aproximam.
                          </div>
                        </div>
                      ),
                    })
                  }
                >
                  <FontAwesomeIcon
                    icon="info-circle"
                    size="sm"
                    color="#BDBDBD"
                  />
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="StatisticsSidebar__Body">
        <div
          className={`StatisticsSidebar__Body__CircularBar ${
            hideCircularProgressBar || !isEnem ? 'hide' : ''
          }`}
        >
          <CircularProgressbar
            onClickInfo={() =>
              setModal({
                open: true,
                className: 'Circular',
                content: (
                  <div className="StatisticsSidebar__Info__Circular">
                    <div className="StatisticsSidebar__Info__Circular__header">
                      <CircularProgressbar
                        onClickInfo={() => {}}
                        onHoverInfo={() => {}}
                        score={100}
                        size={100}
                      />
                    </div>
                    <div className="StatisticsSidebar__Info__Circular__content">
                      Análise automática do texto, baseada em indicadores que
                      compõem a estrutura da redação (quantidade de palavras,
                      parágrafos, conectivos, etc.). Ela varia, para mais ou
                      para menos, de acordo com as modificações realizadas no
                      texto.
                    </div>
                  </div>
                ),
              })
            }
            onHoverInfo={() => {}}
            score={Math.round(iLetrus)}
            size={116}
          />
        </div>
        <div
          className={`StatisticsSidebar__Body__Feedbacks ${
            feedbacks && feedbacks.size > 0 ? 'show' : ''
          }`}
        >
          {feedbacks &&
            feedbacks.map((feedback: ImmutableMap<any>) => {
              return (
                <DevolutiveCard key={feedback.get('comment')} showShadows>
                  <div
                    className="StatisticsSidebar__Body__Feedbacks__item"
                    dangerouslySetInnerHTML={{__html: feedback.get('comment')}}
                  />
                </DevolutiveCard>
              );
            })}
        </div>
        {composition.size &&
        composition.getIn(['test', 'test_type']) !== 'Especial' ? (
          <>
            {' '}
            <div className="StatisticsSidebar__Body__ProgressBar">
              {renderProgress()}
            </div>
            <div
              className={`StatisticsSidebar__Body__Deviations ${
                hasDeviations && !isHidingPrincipalDeviations ? 'show' : ''
              }`}
            >
              <h3 className="StatisticsSidebar__Body__Deviations__Header">
                Principais desvios
              </h3>
              <div className="StatisticsSidebar__Body__Deviations__Content">
                <div
                  className={`StatisticsSidebar__Body__Deviations__Content__List ${
                    orthographicDeviations.count() ? 'show' : ''
                  }`}
                >
                  <div className="StatisticsSidebar__Body__Deviations__Content__List__title">
                    Ortografia:
                  </div>
                  {orthographicDeviations.map((deviation, index) => {
                    return (
                      <div
                        title={deviation}
                        className="StatisticsSidebar__Body__Deviations__Content__List__item"
                        key={parseInt(index, 10)}
                      >
                        {deviation}
                      </div>
                    );
                  })}
                </div>
                <div
                  className={`StatisticsSidebar__Body__Deviations__Content__List ${
                    oralityDeviations.count() ? 'show' : ''
                  }`}
                >
                  <div className="StatisticsSidebar__Body__Deviations__Content__List__title">
                    Oralidade:
                  </div>
                  {oralityDeviations.map((deviation, index) => {
                    return (
                      <div
                        title={deviation}
                        className="StatisticsSidebar__Body__Deviations__Content__List__item"
                        key={parseInt(index, 10)}
                      >
                        {deviation}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      <Modal
        title="O que são os níveis?"
        isOpen={modal.open}
        onClose={() => setModal({open: false, className: ''})}
        buttonClose
        clickOutsideToClose
        userRole="student"
        vw={modal.isPedagogicalContent ? 90 : undefined}
        vh={modal.isPedagogicalContent ? 90 : undefined}
      >
        <div
          className={`StatisticsSidebar__Modal ${
            modal.isPedagogicalContent ? 'pedagogicalContent' : ''
          }`}
        >
          {modal.content}
        </div>
      </Modal>
    </section>
  );
}

export default connect((state: ApplicationState) => ({
  isFundII: getCompositionIsFundII(state),
  learningPath: getLearningPath(state),
  iLetrusScore: getIletrusScore(state),
}))(StatisticsSidebar);
